import { FileDownloadOutlined, UploadFileRounded } from '@mui/icons-material';
import {
  Button,
  Checkbox,
  Table,
  TableBody,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { SELECT_ACTION, adminTab } from '~/types/enum';
import { cell } from '../ResourceTable/ResourceTable.style';
import { uniq, uniqueId } from 'lodash';
import { TSort } from '~/types/common.type';
import { useSelector } from 'react-redux';
import { useEffect, useRef, useState } from 'react';
import { AdminDialog } from '~/components/Admin/AdminDialog/AdminDialog';
import { HEADERS } from '~/views/Admin/Admin.constant';
import { admin } from '~/views/Admin/Admin.style';
import { InternetResourceRow } from '../InternetResourceTable/InternetResourceRow';
import { InternetTableHeaderCell } from '~/components/Admin/InternetTableHeaderCell';
import {
  getterAdminInternetResourcesPayload,
  getterInternetDownloadList,
  getterInternetResourceFilter,
  getterInternetResources,
} from '~/store/internetResource/internetResourceGetter';
import { TAdminInternetResource } from '~/store/internetResource/internetResource.type';
import {
  useGetInternetResourcesQuery,
  useUpdateInternetLaunchStatusMutation,
} from '~/api/oneBoxV3';
import { useAppDispatch } from '~/store';
import { setSearchCondition } from '~/store/internetResource';
import { ACTION_STATUS } from '../ResourceTable/ResourceTable';
import { buildData, handleExportExcel } from '~/utils/helper/file';
import { getTitleText } from '~/utils/helper/display';
import { InternetEditDialogContent } from '../AdminDialog/EditDialogContent';
import { UploadDialogContent } from '../AdminDialog/UploadDialogContent';
import { AdminDialogActions } from '../AdminDialog/AdminDialogActions';
import { BaseSnackbar } from '~/components/Global/Snackbar';

type TProps = {
  sort: TSort<string>;
  handleSort: (property: string) => void;
  tab: adminTab;
  setTotalPageCount: (count: number) => void;
};

export type THandleSelectedResources = {
  type: SELECT_ACTION;
  resource?: TAdminInternetResource;
};

export const InternetResourceTable = ({
  sort,
  handleSort,
  tab,
  setTotalPageCount,
}: TProps) => {
  const [showSnackbar, setShowSnackbar] = useState({
    isShow: false,
    message: '',
  });
  const dispatch = useAppDispatch();
  const { isLaunch, pagination } = useSelector(getterInternetResourceFilter);
  const resourcePayload = useSelector(getterAdminInternetResourcesPayload);
  const [actionStatus, setActionStatus] = useState<ACTION_STATUS>(
    ACTION_STATUS.NON,
  );
  const [selectedResources, setSelectedResources] = useState<
    TAdminInternetResource[]
  >([]);
  const memoizedPageRef = useRef({
    launched: { page: 1, pageSize: 10 },
    notLaunched: { page: 1, pageSize: 10 },
  });

  useGetInternetResourcesQuery(resourcePayload, {
    refetchOnMountOrArgChange: true,
  });

  const { resources, totalResourcesCount } = useSelector(state =>
    getterInternetResources(state, resourcePayload),
  );

  useEffect(() => {
    setTotalPageCount(totalResourcesCount);
  }, [totalResourcesCount]);

  const downloadList = useSelector(state =>
    getterInternetDownloadList(state, resourcePayload),
  );

  const handleSelectedResources = ({
    type,
    resource,
  }: THandleSelectedResources) => {
    switch (type) {
      case SELECT_ACTION.ALL:
        setSelectedResources(resources);
        break;
      case SELECT_ACTION.DELETE:
        resource &&
          setSelectedResources(prev =>
            prev.filter(res => res._id !== resource._id),
          );
        break;
      case SELECT_ACTION.RESET:
        setSelectedResources([]);
        break;
      case SELECT_ACTION.ADD:
        resource && setSelectedResources(prev => uniq([...prev, resource]));
        break;

      case SELECT_ACTION.EDIT:
        resource && setSelectedResources([resource]);
        break;
      default:
        break;
    }
  };

  type THandleFileDownload = Omit<TAdminInternetResource, 'isLaunch' | '_id'>;
  const handleFileDownload = (downloadData: THandleFileDownload[]) => {
    const csvTitle = [
      '類別',
      '學年度',
      '學制',
      '科目',
      '年級',
      '資源類別1',
      '資源類別2',
      '資源名稱',
      'URL',
    ];
    const res = buildData<THandleFileDownload>(downloadData, csvTitle);
    handleExportExcel(res);
  };

  const handleTabChange = (
    event: React.MouseEvent<HTMLElement>,
    newValue: boolean | null,
  ) => {
    // exclusive 的 ToggleButtonGroup 在重複點選已選取項目時會帶 null，
    // 直接送出會清空上架狀態篩選，這裡忽略不處理
    if (newValue === null) return;
    const { page, pageSize } = pagination;
    if (isLaunch) {
      memoizedPageRef.current.launched = { page, pageSize };
    } else {
      memoizedPageRef.current.notLaunched = { page, pageSize };
    }
    const { launched, notLaunched } = memoizedPageRef.current;
    dispatch(
      setSearchCondition({
        isLaunch: newValue,
        page: isLaunch ? notLaunched.page : launched.page,
        pageSize: isLaunch ? notLaunched.pageSize : launched.pageSize,
      }),
    );
  };

  useEffect(() => {
    setActionStatus(ACTION_STATUS.NON);
    handleSelectedResources({ type: SELECT_ACTION.RESET });
  }, [tab]);

  const [updateInternetLaunchApi] = useUpdateInternetLaunchStatusMutation();

  const handleClose = () => {
    switch (actionStatus) {
      case ACTION_STATUS.BATCH_OFF:
      case ACTION_STATUS.BATCH_ON:
        setActionStatus(ACTION_STATUS.SELECT);
        break;
      case ACTION_STATUS.EDIT:
        setActionStatus(ACTION_STATUS.NON);
        handleSelectedResources({ type: SELECT_ACTION.RESET });
        break;
      default:
        setActionStatus(ACTION_STATUS.NON);
    }
  };

  const handleSubmit = async () => {
    const idList = selectedResources.map(res => res._id);
    await updateInternetLaunchApi({
      ids: idList,
      isLaunch: actionStatus === ACTION_STATUS.BATCH_ON,
    });

    setShowSnackbar({
      message: `資源已${actionStatus === ACTION_STATUS.BATCH_ON ? '上' : '下'}架成功！`,
      isShow: true,
    });
    setActionStatus(ACTION_STATUS.NON);
    handleSelectedResources({ type: SELECT_ACTION.RESET });
  };

  return (
    <>
      <admin.TopContainer>
        <ToggleButtonGroup
          color="info"
          value={isLaunch}
          exclusive
          onChange={handleTabChange}
          aria-label="Platform"
        >
          <ToggleButton value={false}>未上架</ToggleButton>
          <ToggleButton value={true}>已上架</ToggleButton>
        </ToggleButtonGroup>
        <Typography variant="h6" sx={{ marginRight: 'auto' }}>
          {tab}
        </Typography>
        {tab !== adminTab.record && (
          <admin.ActionContainer>
            {selectedResources.length > 0 ? (
              <>
                <Button
                  color="inherit"
                  variant="outlined"
                  onClick={() => {
                    setActionStatus(ACTION_STATUS.NON);
                    handleSelectedResources({ type: SELECT_ACTION.RESET });
                  }}
                >
                  清除選取
                </Button>
                <Button
                  variant="contained"
                  color={!isLaunch ? 'info' : 'success'}
                  sx={{
                    '&:hover': {
                      backgroundColor: !isLaunch ? 'info.dark' : 'success.dark',
                    },
                  }}
                  onClick={() => {
                    setActionStatus(prev => {
                      if (prev === ACTION_STATUS.SELECT) {
                        return isLaunch
                          ? ACTION_STATUS.BATCH_OFF
                          : ACTION_STATUS.BATCH_ON;
                      }
                      return prev;
                    });
                  }}
                >
                  批次 {isLaunch ? '下' : '上'}架
                </Button>
              </>
            ) : (
              <>
                <admin.IconButton
                  variant="outlined"
                  color="inherit"
                  onClick={() => {
                    handleFileDownload(downloadList);
                  }}
                >
                  <FileDownloadOutlined />
                  表單下載
                </admin.IconButton>
                <admin.IconButton
                  variant="outlined"
                  color="inherit"
                  onClick={() => {
                    setActionStatus(ACTION_STATUS.UPLOAD);
                  }}
                >
                  <UploadFileRounded />
                  表單上傳
                </admin.IconButton>
              </>
            )}
          </admin.ActionContainer>
        )}
      </admin.TopContainer>
      <Table>
        <TableHead>
          <TableRow>
            {
              <cell.Container>
                <Checkbox
                  checked={
                    selectedResources.length === resources.length &&
                    selectedResources.length !== 0 &&
                    resources.length !== 0
                  }
                  indeterminate={
                    selectedResources.length > 0 &&
                    selectedResources.length < resources.length
                  }
                  sx={{ padding: '0px' }}
                  color="info"
                  onChange={(event, checked) => {
                    setActionStatus(() => {
                      return checked ? ACTION_STATUS.SELECT : ACTION_STATUS.NON;
                    });
                    handleSelectedResources({
                      type: checked ? SELECT_ACTION.ALL : SELECT_ACTION.RESET,
                    });
                  }}
                />
              </cell.Container>
            }
            {HEADERS.InternetResource.filter(item =>
              actionStatus === ACTION_STATUS.NON ? true : !!item.title,
            ).map((header, index) => (
              <InternetTableHeaderCell
                key={uniqueId()}
                data={header}
                index={index}
                sort={sort}
                handleSort={handleSort}
              />
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {resources.map(data => (
            <InternetResourceRow
              key={data._id}
              data={data}
              isSelected={
                !!selectedResources.find(
                  selectedItem => selectedItem._id === data._id,
                )
              }
              handleSelectedResources={handleSelectedResources}
              selectedResourcesLength={selectedResources.length}
              setActionStatus={setActionStatus}
            />
          ))}
        </TableBody>
      </Table>

      <AdminDialog
        open={
          actionStatus !== ACTION_STATUS.NON &&
          actionStatus !== ACTION_STATUS.SELECT
        }
        handleClose={handleClose}
        status={actionStatus}
        titleText={getTitleText(actionStatus, selectedResources.length)}
      >
        {actionStatus === ACTION_STATUS.UPLOAD && (
          <UploadDialogContent handleClose={handleClose} isLaunch={isLaunch} />
        )}
        {actionStatus === ACTION_STATUS.EDIT &&
          selectedResources.length === 1 && (
            <InternetEditDialogContent
              editedResource={selectedResources[0]}
              handleClose={handleClose}
            />
          )}
        {(actionStatus === ACTION_STATUS.BATCH_OFF ||
          actionStatus === ACTION_STATUS.BATCH_ON) && (
          <AdminDialogActions
            status={actionStatus}
            handleClose={handleClose}
            handleSubmit={handleSubmit}
            acceptButtonText={`確定
              ${actionStatus === ACTION_STATUS.BATCH_OFF ? '下' : ''}
              ${actionStatus === ACTION_STATUS.BATCH_ON ? '上' : ''}
              架`}
          />
        )}
      </AdminDialog>
      <BaseSnackbar
        open={showSnackbar.isShow}
        message={showSnackbar.message}
        handleClose={() => setShowSnackbar({ isShow: false, message: '' })}
      />
    </>
  );
};
